import { FC, ReactNode, useMemo, useCallback, ChangeEvent } from 'react'
import { FutureInputDecoration } from 'components/features/future/FutureInputDecoration/FutureInputDecoration'
import { FutureInputWithControl } from 'components/features/future/FutureInputDecoration/FutureInputWithControl'
import { useFormContext } from 'react-hook-form'
import { InputRangeNative } from 'components/features/pfm/InputRangeNative/InputRangeNative'
import { MaskOptions, MaskedInputProps, MaskedInputController } from 'components/atoms/inputs/maskedInput/MaskedInput/MaskedInput'
import { FutureInput } from 'components/atoms/inputs/FutureInput/FutureInput'
import { PremiumInput } from 'components/features/future/FutureField/PremiumInput'
import { isMemberPremiumSelector } from 'selectors/globalSelectors'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import IMask from 'imask'
import { ThemeProvider } from 'components/features/theme/ThemeProvider/ThemeProvider'

type RangeProps = {
  min: number
  max: number
  step: number
}

type FutureFieldSliderProps =
  & RangeProps
  & {
    mask: MaskOptions
    name: string
    label?: string
    error?: string
    units?: string
    note?: ReactNode
    inputMode?: MaskedInputProps['inputMode']
    isPremium?: boolean
    isDisabled?: boolean
  }
export const FutureFieldSlider: FC<FutureFieldSliderProps> = (props) => {
  const { label, error, note, name, units, min, max, step, mask, isPremium, isDisabled, inputMode } = props

  const isMemberPremium = useStoreSelector(isMemberPremiumSelector)
  const isPremiumRequired = useMemo(() => !!(isPremium && !isMemberPremium), [isMemberPremium, isPremium])

  const { setValue, watch } = useFormContext<Record<string, any>>()
  const value = watch(name) as string

  const handleChange = useCallback<(event: ChangeEvent<HTMLInputElement>) => void>((event) => {
    const targetValue = +event.target.value
    setValue(name, targetValue, { shouldDirty: true, shouldValidate: true })
  }, [name, setValue])

  const controlChild = useMemo(() => {
    const nextValue = parseFloat(value) || 0
    return (
      <InputRangeNative
        value={ nextValue }
        min={ min }
        max={ max }
        step={ step }
        theme='small'
        tabIndex={ -1 }
        disabled={ isPremiumRequired || isDisabled }
        onChange={ handleChange }
      />
    )
  }, [handleChange, max, min, step, value, isPremiumRequired, isDisabled])

  const inputChild = useMemo(() => isPremiumRequired ? (
    <PremiumInput name={ name } value={ value }>
      { IMask.pipe(`${ value }`, mask as IMask.AnyMask) }
    </PremiumInput>
  ) : (
    <div style={{ width: max <= 100 && !units ? '92px' : '128px' }}>
      <MaskedInputController options={ mask } name={ name } renderer={ FutureInput } inputMode={ inputMode } />
    </div>
  ), [isPremiumRequired, max, units, mask, name, inputMode, value])

  return (
    <ThemeProvider inputDisabled={ isDisabled }>
      <FutureInputDecoration
        label={ label }
        error={ error }
        note={ note }
        units={ units }
      >
        <FutureInputWithControl control={ controlChild }>
          { inputChild }
        </FutureInputWithControl>
      </FutureInputDecoration>
    </ThemeProvider>
  )
}
